/**
 * SiteAdminCard — card compacto de SITE (loja / depósito / hospital)
 * usado no split panel de Admin → Sites.
 */

import { MapPin, Pencil, Trash2 } from "lucide-react";
import clsx from "clsx";

import type { Site } from "@/types";

import { Badge } from "./Badge";
import { Button } from "./Button";
import { formatDate } from "@/lib/format";

interface Props {
  site: Site;
  /** Nome do tenant dono — mostrado como chip quando super_admin */
  tenantName?: string | null;
  /** Quantidade de equipments ativos vinculados */
  equipmentsCount: number;
  /** Quantidade de devices ativos vinculados */
  devicesCount: number;
  onEdit?: () => void;
  onDelete?: () => void;
}

export function SiteAdminCard({
  site, tenantName, equipmentsCount, devicesCount, onEdit, onDelete,
}: Props) {
  return (
    <div
      className={clsx(
        "flex flex-col gap-3 rounded-lg border p-4 transition-shadow",
        "border-surface-200 dark:border-slate-800",
        "bg-white dark:bg-slate-900 hover:shadow-card-hover",
      )}
    >
      {/* Header — nome + tenant chip */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 flex-none text-brand-600 dark:text-brand-400" />
            <span className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
              {site.name}
            </span>
          </div>
          <div className="mt-1">
            <code className="text-[11px] text-slate-500 dark:text-slate-400">
              {site.slug}
            </code>
          </div>
        </div>
        {tenantName && (
          <Badge tone="brand">{tenantName}</Badge>
        )}
      </div>

      {/* Endereço */}
      {site.address && (
        <div className="text-xs text-slate-600 dark:text-slate-400">
          {site.address}
        </div>
      )}

      {/* Contagens */}
      <div className="grid grid-cols-2 gap-3 border-t border-surface-100 dark:border-slate-800 pt-3 text-xs">
        <div>
          <div className="text-slate-500 dark:text-slate-400">Ativos</div>
          <div className="tabular-nums text-slate-700 dark:text-slate-300">
            {equipmentsCount}
          </div>
        </div>
        <div>
          <div className="text-slate-500 dark:text-slate-400">Devices</div>
          <div className="tabular-nums text-slate-700 dark:text-slate-300">
            {devicesCount}
          </div>
        </div>
      </div>

      {/* Criado em */}
      {site.created_at && (
        <div className="text-[10px] text-slate-400 dark:text-slate-500">
          Criado em {formatDate(site.created_at)}
        </div>
      )}

      {/* Ações */}
      <div className="mt-auto flex items-center justify-end gap-1">
        {onEdit && (
          <Button
            size="sm"
            variant="ghost"
            icon={<Pencil className="h-3.5 w-3.5" />}
            onClick={onEdit}
          >
            Editar
          </Button>
        )}
        {onDelete && (
          <Button
            size="sm"
            variant="ghost"
            icon={<Trash2 className="h-3.5 w-3.5 text-red-500" />}
            onClick={onDelete}
          />
        )}
      </div>
    </div>
  );
}
